import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../lib/api';
import type { ToolKey } from '../../../../lib/session';
import type { SeatSummary } from '../../billing/api';

/**
 * Users table, teams, roles, invitations and deactivation (docs/dev/api-users.md; Einstellungen 27, Admin 06).
 * Every query lives under the root `['users']`, which `user.updated` and `billing.updated` refresh.
 */

export interface ActionState {
  allowed: boolean;
  reason: string | null;
}

export interface UserTableRow {
  kind: 'user' | 'invitation';
  id: string;
  name: string;
  email: string;
  roleId: string;
  roleLabel: string;
  teamId: string | null;
  managerId: string | null;
  /** External company of the membership (0037) — shown as „extern · <Firma>", no restriction. */
  externalCompany: string | null;
  status: 'active' | 'invited' | 'deactivated';
  /** „Aktiv" | „Einladung offen" | „Einladung abgelaufen" | „Deaktiviert" */
  statusLabel: string;
  ownBilling: { value: boolean; label: string; locked: boolean; reason: string | null };
  /** Resolved `tools.<tool>` values (PKV M0, `PUT /users/:id/tools`); null for an invitation. */
  tools: Record<ToolKey, 'yes' | 'no'> | null;
  /** The parent organization that manages this servicing membership (0045); null for an own member. */
  servicingFrom: { id: string; name: string } | null;
  action: { key: 'self' | 'edit' | 'resend' | 'reactivate'; label: string; allowed: boolean };
}

export interface UsersTable {
  headline: string;
  footer: string;
  seats: SeatSummary;
  rows: UserTableRow[];
  actions: { invite: ActionState };
}

export interface RoleItem {
  id: string;
  key: string;
  name: string;
  isSystem: boolean;
  userCount: number;
  invitableByTeamLead: boolean;
}

export interface TeamItem {
  id: string;
  name: string;
  leadUserId: string | null;
  leadName: string | null;
  parentTeamId: string | null;
  memberCount: number;
}

export interface PatchUserBody {
  roleId?: string | undefined;
  teamId?: string | null | undefined;
  managerId?: string | null | undefined;
  jobTitle?: string | null | undefined;
  hasOwnBilling?: boolean | undefined;
  externalCompany?: string | null | undefined;
}

export interface InvitationRowInput {
  email: string;
  firstName?: string | undefined;
  lastName?: string | undefined;
  roleId: string;
  hasOwnBilling: boolean;
  externalCompany?: string | null | undefined;
}

export interface InvitationResultRow {
  email: string;
  status: 'sent' | 'error';
  code: string | null;
  message: string | null;
  hint: string | null;
}

export interface DeactivationPreview {
  user: { id: string; name: string; shortName: string; roleLabel: string };
  title: string;
  counts: {
    ownedContacts: number;
    activeDeals: number;
    openTasks: number;
    futureAppointments: number;
    assignedFinancings: number;
    openCommission: { cents: number; label: string };
  };
  appointmentsLabel: string;
  successors: { id: string; name: string; shortName: string; roleLabel: string }[];
  defaultSuccessorId: string | null;
  rebookAppointmentsDefault: boolean;
}

export const userKeys = {
  table: () => ['users', 'table'] as const,
  teams: () => ['users', 'teams'] as const,
  roles: () => ['users', 'roles'] as const,
  deactivation: (id: string) => ['users', 'deactivation', id] as const,
};

export function useUsersTable() {
  return useQuery({
    queryKey: userKeys.table(),
    queryFn: () => api<UsersTable>('/settings/organization/users'),
    retry: false,
  });
}

export function useRoles(enabled = true) {
  return useQuery({
    queryKey: userKeys.roles(),
    queryFn: () => api<{ items: RoleItem[] }>('/roles'),
    select: (data) => data.items,
    enabled,
  });
}

export function useTeams(enabled = true) {
  return useQuery({
    queryKey: userKeys.teams(),
    queryFn: () => api<{ items: TeamItem[] }>('/teams'),
    select: (data) => data.items,
    enabled,
  });
}

export function useDeactivationPreview(id: string | null) {
  return useQuery({
    queryKey: userKeys.deactivation(id ?? ''),
    queryFn: () => api<DeactivationPreview>(`/users/${id}/deactivation`),
    enabled: id !== null,
    staleTime: 0,
  });
}

/** Replaces one row of the cached table with the server's answer. */
function useApplyRow() {
  const queryClient = useQueryClient();
  return (row: UserTableRow) => {
    queryClient.setQueryData<UsersTable>(userKeys.table(), (table) =>
      table ? { ...table, rows: table.rows.map((entry) => (entry.id === row.id ? row : entry)) } : table,
    );
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };
}

export function usePatchUser() {
  const apply = useApplyRow();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchUserBody }) =>
      api<UserTableRow>(`/users/${id}`, { method: 'PATCH', body }),
    onSuccess: apply,
  });
}

export function useReactivateUser() {
  const apply = useApplyRow();
  return useMutation({
    mutationFn: (id: string) => api<UserTableRow>(`/users/${id}/reactivate`, { method: 'POST' }),
    onSuccess: apply,
  });
}

export function useResendInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<unknown>(`/invitations/${id}/resend`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (row: InvitationRowInput) =>
      api<{ rows: InvitationResultRow[]; sent: number }>('/invitations', {
        method: 'POST',
        body: { rows: [row], source: 'settings' },
      }),
    onSuccess: (result) => {
      if (result.sent > 0) void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, successorId, rebookAppointments }: DeactivateInput) =>
      api<{ userId: string; successorId: string }>(`/users/${id}/deactivate`, {
        method: 'POST',
        body: { successorId, rebookAppointments },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

interface DeactivateInput {
  id: string;
  successorId: string | null;
  rebookAppointments: boolean;
}
